/**
 * Customer market access: sync assigned_market metafield, redirect logged-in
 * customers after login, or logout when switching markets via country selector.
 */
(function customerMarketAccess() {
  const STORAGE_NOTICE_KEY = 'pmecake.marketRedirectNotice';
  const STORAGE_LOGOUT_NOTICE_KEY = 'pmecake.show_logout_notice';
  const STORAGE_LOGOUT_NOTICE_MESSAGE_KEY = 'pmecake.logout_notice_message';
  const STORAGE_LOGOUT_NOTICE_CLOSE_KEY = 'pmecake.logout_notice_close';
  const STORAGE_LOOP_GUARD_KEY = 'pmecake.marketRedirectGuard';
  const LOOP_GUARD_MS = 10000;

  function injectStyles() {
    if (document.getElementById('pmecake-market-access-styles')) return;

    const style = document.createElement('style');
    style.id = 'pmecake-market-access-styles';
    style.textContent = `
      .pmecake-market-access-notice {
        position: relative;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.875rem 1.25rem;
        background: #0872bf;
        color: #fff;
        font-size: 0.9375rem;
        line-height: 1.5;
      }

      .pmecake-market-access-notice__text {
        margin: 0;
        flex: 1;
      }

      .pmecake-market-access-notice__close {
        appearance: none;
        border: 0;
        background: transparent;
        color: inherit;
        font-size: 1.5rem;
        line-height: 1;
        cursor: pointer;
        padding: 0 0.25rem;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * @param {string} message
   * @param {{ closeLabel?: string, onClose?: () => void }} [options]
   */
  function showNoticeBanner(message, options = {}) {
    if (!message) return;
    if (document.getElementById('pmecake-market-access-notice')) return;

    const banner = document.createElement('div');
    banner.id = 'pmecake-market-access-notice';
    banner.setAttribute('role', 'status');
    banner.className = 'pmecake-market-access-notice';

    const text = document.createElement('p');
    text.className = 'pmecake-market-access-notice__text';
    text.textContent = message;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'pmecake-market-access-notice__close';
    closeButton.setAttribute('aria-label', options.closeLabel || 'Close');
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => {
      banner.remove();
      options.onClose?.();
    });

    banner.appendChild(text);
    banner.appendChild(closeButton);
    document.body.prepend(banner);
  }

  function initGuestLogoutNotice() {
    function run() {
      if (sessionStorage.getItem(STORAGE_LOGOUT_NOTICE_KEY) !== '1') return;

      const message = sessionStorage.getItem(STORAGE_LOGOUT_NOTICE_MESSAGE_KEY);
      if (!message) {
        sessionStorage.removeItem(STORAGE_LOGOUT_NOTICE_KEY);
        return;
      }

      const closeLabel =
        sessionStorage.getItem(STORAGE_LOGOUT_NOTICE_CLOSE_KEY) || 'Close';

      injectStyles();
      showNoticeBanner(message, { closeLabel });

      sessionStorage.removeItem(STORAGE_LOGOUT_NOTICE_KEY);
      sessionStorage.removeItem(STORAGE_LOGOUT_NOTICE_MESSAGE_KEY);
      sessionStorage.removeItem(STORAGE_LOGOUT_NOTICE_CLOSE_KEY);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  initGuestLogoutNotice();

  /** @type {any} */
  const config = window.PmecakeMarketAccess;
  if (!config) return;

  /**
   * @param {string | null | undefined} handle
   * @returns {string}
   */
  function normalizeMarket(handle) {
    if (!handle) return '';
    const normalized = String(handle).trim().toLowerCase();
    if (normalized === 'gb') return 'united-kingdom';
    return normalized;
  }

  /**
   * @param {string} referrer
   * @returns {boolean}
   */
  function isPostLoginMismatch(referrer) {
    if (!referrer) return true;
    if (referrer.includes('/account')) return true;
    try {
      return new URL(referrer).origin !== window.location.origin;
    } catch {
      return true;
    }
  }

  /**
   * @param {string} assignedMarket
   * @returns {string | null}
   */
  function buildRedirectUrl(assignedMarket) {
    const prefixes = config.marketPrefixes || {};
    const targetPrefix = prefixes[assignedMarket];
    if (!targetPrefix) return null;

    const normalizedPrefix = targetPrefix.endsWith('/')
      ? targetPrefix.slice(0, -1)
      : targetPrefix;

    const url = new URL(window.location.origin + normalizedPrefix);
    const redirectParam = config.redirectQueryParam || 'market_redirect';
    url.searchParams.set(redirectParam, '1');

    return url.href;
  }

  /**
   * @param {string} assignedMarket
   */
  function redirectToAssignedMarket(assignedMarket) {
    const redirectUrl = buildRedirectUrl(assignedMarket);
    if (!redirectUrl) return;

    if (redirectUrl === window.location.href) return;

    const guardRaw = sessionStorage.getItem(STORAGE_LOOP_GUARD_KEY);
    if (guardRaw) {
      try {
        const guard = JSON.parse(guardRaw);
        if (
          guard.url === redirectUrl &&
          Date.now() - guard.at < LOOP_GUARD_MS
        ) {
          return;
        }
      } catch {
        // Ignore malformed guard payload.
      }
    }

    sessionStorage.setItem(
      STORAGE_LOOP_GUARD_KEY,
      JSON.stringify({ url: redirectUrl, at: Date.now() })
    );
    sessionStorage.setItem(STORAGE_NOTICE_KEY, '1');

    window.location.replace(redirectUrl);
  }

  /**
   * @param {string} currentMarket
   * @returns {Promise<string>}
   */
  function syncAssignedMarket(currentMarket) {
    const syncUrl = new URL(config.syncUrl, window.location.origin);
    syncUrl.searchParams.set('current_market', currentMarket);

    return fetch(syncUrl.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) return '';
        return response.json();
      })
      .then((payload) => normalizeMarket(payload?.assignedMarket))
      .catch(() => '');
  }

  function shouldShowNotice() {
    const redirectParam = config.redirectQueryParam || 'market_redirect';
    const params = new URLSearchParams(window.location.search);
    return (
      params.get(redirectParam) === '1' ||
      sessionStorage.getItem(STORAGE_NOTICE_KEY) === '1'
    );
  }

  function cleanRedirectQueryParam() {
    const redirectParam = config.redirectQueryParam || 'market_redirect';
    const url = new URL(window.location.href);
    if (!url.searchParams.has(redirectParam)) return;

    url.searchParams.delete(redirectParam);
    window.history.replaceState({}, '', url.toString());
  }

  function showWelcomeNoticeBanner() {
    const message = config.messages?.notice;
    if (!message) return;

    showNoticeBanner(message, {
      closeLabel: config.messages?.close || 'Close',
      onClose: () => sessionStorage.removeItem(STORAGE_NOTICE_KEY),
    });

    sessionStorage.removeItem(STORAGE_NOTICE_KEY);
    cleanRedirectQueryParam();
  }

  /**
   * @param {string} assignedMarket
   * @param {string} currentMarket
   */
  function enforceMarketAccess(assignedMarket, currentMarket) {
    if (!assignedMarket || !currentMarket) return;
    if (assignedMarket === currentMarket) return;

    const referrer = document.referrer;

    if (isPostLoginMismatch(referrer)) {
      redirectToAssignedMarket(assignedMarket);
      return;
    }

    sessionStorage.setItem(STORAGE_LOGOUT_NOTICE_KEY, '1');
    sessionStorage.setItem(
      STORAGE_LOGOUT_NOTICE_MESSAGE_KEY,
      config.messages?.logoutNotice || ''
    );
    sessionStorage.setItem(
      STORAGE_LOGOUT_NOTICE_CLOSE_KEY,
      config.messages?.close || 'Close'
    );

    const currentCleanUrl =
      window.location.origin + window.location.pathname;
    const logoutBase = config.logoutUrl || '/account/logout';
    const separator = logoutBase.includes('?') ? '&' : '?';
    window.location.href = `${logoutBase}${separator}return_url=${encodeURIComponent(currentCleanUrl)}`;
  }

  function initBanner() {
    if (!shouldShowNotice()) return;
    injectStyles();
    showWelcomeNoticeBanner();
  }

  function initAccessControl() {
    if (!config.customerLoggedIn) return;

    let assignedMarket = normalizeMarket(config.assignedMarket);
    const currentMarket = normalizeMarket(config.currentMarket);

    if (assignedMarket) {
      enforceMarketAccess(assignedMarket, currentMarket);
      return;
    }

    if (!currentMarket) return;

    syncAssignedMarket(currentMarket).then((syncedMarket) => {
      if (!syncedMarket) return;
      config.assignedMarket = syncedMarket;
      enforceMarketAccess(syncedMarket, currentMarket);
    });
  }

  initAccessControl();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBanner);
  } else {
    initBanner();
  }
})();
